"use client";

import { motion } from "framer-motion";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Award,
  Star,
  BookOpen,
  Shield,
  Users,
  ArrowRight,
  CheckCircle,
  Anchor,
  Compass,
  GraduationCap,
} from "lucide-react";
import { coursesData } from "../data/courses";

const courseCategories = [
  // {
  //   id: "pre-sea-training",
  //   title: "Pre-Sea Training",
  //   description:
  //     "Entry-level courses required before going onboard for the first time",
  //   icon: Ship,
  //   courseCount: coursesData["pre-sea-training"].courses.length,
  //   duration: "2-4 weeks",
  //   level: "Beginner",
  //   gradient: "from-blue-500 to-blue-600",
  //   bgGradient: "from-blue-50 to-blue-100",
  //   accentColor: "blue",
  //   highlights: [
  //     "STCW Basic Package",
  //     "Essential Safety Training",
  //     "Industry Entry Requirements",
  //   ],
  // },
  {
    id: "cadetship-training",
    title: "Cadetship Training",
    description: "Comprehensive training programs for maritime cadets",
    icon: GraduationCap,
    courseCount: 1,
    duration: "6-18 months",
    level: "Beginner",
    gradient: "from-emerald-500 to-emerald-600",
    bgGradient: "from-emerald-50 to-emerald-100",
    accentColor: "emerald",
    highlights: [
      "3.5-month Ship Preparatory Course",
      "9-month Onboard Training (with stipend)",
      "2.5-month Advanced Course & Certificate of Competency",
    ],
  },
  {
    id: "competency-training",
    title: "Certificate of Competency",
    description: "Advanced certification courses for maritime professionals",
    icon: Award,
    courseCount: coursesData["competency-training"].courses.length,
    duration: "3-8 weeks",
    level: "Advanced",
    gradient: "from-purple-500 to-purple-600",
    bgGradient: "from-purple-50 to-purple-100",
    accentColor: "purple",
    highlights: [
      "Officer Certifications",
      "MEO Classes",
      "Professional Advancement",
    ],
  },
  {
    id: "value-added-courses",
    title: "Value-Added Courses",
    description: "Specialized courses to enhance maritime skills and knowledge",
    icon: Star,
    courseCount: coursesData["value-added-courses"].courses.length,
    duration: "1-4 weeks",
    level: "All Levels",
    gradient: "from-amber-500 to-amber-600",
    bgGradient: "from-amber-50 to-amber-100",
    accentColor: "amber",
    highlights: [
      "Specialized Skills",
      "Industry Updates",
      "Career Enhancement",
    ],
  },
  {
    id: "stcw-courses",
    title: "STCW Courses",
    description:
      "Standards of Training, Certification and Watchkeeping courses",
    icon: BookOpen,
    courseCount: coursesData["stcw-courses"].courses.length,
    duration: "1-7 days",
    level: "All Levels",
    gradient: "from-teal-500 to-teal-600",
    bgGradient: "from-teal-50 to-teal-100",
    accentColor: "teal",
    highlights: [
      "Basic & Advanced STCW",
      "Refresher Courses",
      "Simulator Training",
    ],
  },
  {
    id: "offshore-survival",
    title: "Offshore & Survival Courses",
    description: "Safety and survival training for offshore operations",
    icon: Shield,
    courseCount: coursesData["offshore-survival"].courses.length,
    duration: "1-4 days",
    level: "All Levels",
    gradient: "from-red-500 to-red-600",
    bgGradient: "from-red-50 to-red-100",
    accentColor: "red",
    highlights: ["BOSIET Training", "Survival Techniques", "Offshore Safety"],
  },
];

const features = [
  {
    title: "Industry Certified",
    description: "All courses meet international maritime standards",
    icon: Award,
  },
  {
    title: "Expert Instructors",
    description: "Learn from experienced maritime professionals",
    icon: Users,
  },
  {
    title: "Modern Facilities",
    description: "State-of-the-art training equipment and simulators",
    icon: Compass,
  },
  {
    title: "Career Support",
    description: "Job placement assistance and career guidance",
    icon: Anchor,
  },
];

const Courses = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-teal-50/20">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('/placeholder.svg?height=800&width=1200')] bg-cover bg-center"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent"></div>

        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="mb-6 inline-flex items-center rounded-full border border-teal-500/30 bg-teal-500/20 px-4 py-2 text-sm font-medium text-teal-300">
              <Anchor className="mr-2 h-4 w-4" />
              Professional Maritime Training
            </div>

            <h1 className="mb-6 text-5xl font-bold leading-tight text-white sm:text-6xl lg:text-7xl">
              Maritime Training
              <span className="block bg-gradient-to-r from-teal-300 via-blue-300 to-cyan-300 bg-clip-text text-transparent">
                Excellence
              </span>
            </h1>

            <p className="mb-8 max-w-2xl text-xl leading-relaxed text-slate-300">
              Advance your maritime career with our comprehensive training
              programs. From entry-level certifications to advanced competency
              courses, we prepare you for success at sea.
            </p>

            <div className="flex flex-col gap-4 sm:flex-row">
              <a
                href="#courses"
                className="inline-flex items-center justify-center rounded-xl bg-teal-600 px-8 py-4 text-lg font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:bg-teal-700 hover:shadow-xl"
              >
                Explore Certifications
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories Section */}
      <section id="courses" className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-4xl font-bold text-slate-900 sm:text-5xl">
              Choose Your Training Path
            </h2>
            <p className="mx-auto max-w-3xl text-lg leading-relaxed text-slate-600">
              Select from our specialized training categories designed to meet
              industry standards and advance your maritime career at every
              level.
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {courseCategories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Link to={`/courses/${category.id}`}>
                    <div className="group relative h-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
                      {/* Gradient Top Border */}
                      <div
                        className={`h-2 bg-gradient-to-r ${category.gradient}`}
                      ></div>

                      {/* Card Content */}
                      <div className="p-8">
                        {/* Title with Icon */}
                        <div className="mb-4 flex items-center space-x-4">
                          <div
                            className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${category.gradient} flex-shrink-0 text-white shadow-lg transition-transform duration-300 group-hover:scale-110`}
                          >
                            <IconComponent size={24} />
                          </div>
                          <h3 className="text-xl font-bold text-slate-900 transition-colors duration-300 group-hover:text-teal-600">
                            {category.title}
                          </h3>
                        </div>

                        <p className="mb-6 leading-relaxed text-slate-600">
                          {category.description}
                        </p>

                        {/* Stats Grid */}
                        {/* <div className="mb-6 grid grid-cols-3 gap-4">
                          <div
                            className={`rounded-xl bg-gradient-to-br ${category.bgGradient} p-3 text-center`}
                          >
                            <div className="mb-1 flex items-center justify-center">
                              <BookOpen
                                size={16}
                                className={`text-${category.accentColor}-600`}
                              />
                            </div>
                            <div
                              className={`text-sm font-bold text-${category.accentColor}-700`}
                            >
                              {category.courseCount}
                            </div>
                            <div className="text-xs text-slate-500">
                              Courses
                            </div>
                          </div>
                          <div
                            className={`rounded-xl bg-gradient-to-br ${category.bgGradient} p-3 text-center`}
                          >
                            <div className="mb-1 flex items-center justify-center">
                              <Clock
                                size={16}
                                className={`text-${category.accentColor}-600`}
                              />
                            </div>
                            <div
                              className={`text-sm font-bold text-${category.accentColor}-700`}
                            >
                              {category.duration}
                            </div>
                            <div className="text-xs text-slate-500">
                              Duration
                            </div>
                          </div>
                          <div
                            className={`rounded-xl bg-gradient-to-br ${category.bgGradient} p-3 text-center`}
                          >
                            <div className="mb-1 flex items-center justify-center">
                              <TrendingUp
                                size={16}
                                className={`text-${category.accentColor}-600`}
                              />
                            </div>
                            <div
                              className={`text-sm font-bold text-${category.accentColor}-700`}
                            >
                              {category.level}
                            </div>
                            <div className="text-xs text-slate-500">Level</div>
                          </div>
                        </div> */}

                        {/* Highlights */}
                        <div className="mb-6">
                          <h4 className="mb-3 text-sm font-semibold text-slate-700">
                            Key Highlights:
                          </h4>
                          <ul className="space-y-2">
                            {category.highlights.map((highlight, idx) => (
                              <li
                                key={idx}
                                className="flex items-center text-sm text-slate-600"
                              >
                                <CheckCircle
                                  size={14}
                                  className="mr-2 flex-shrink-0 text-teal-500"
                                />
                                {highlight}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* CTA */}
                        <div className="flex items-center justify-between border-t border-slate-100 pt-4">
                          <span className="text-sm font-medium text-teal-600">
                            View All Courses
                          </span>
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-50 transition-all duration-300 group-hover:scale-110 group-hover:bg-teal-100">
                            <ArrowRight className="h-4 w-4 text-teal-600" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="border-y border-slate-200 bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl">
              Training Excellence
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-slate-600">
              Industry-leading training programs designed for maritime
              excellence and career advancement
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group text-center"
              >
                <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-lg transition-transform duration-300 group-hover:scale-110">
                  <feature.icon className="h-8 w-8" />
                </div>
                <h3 className="mb-3 text-xl font-semibold text-slate-900">
                  {feature.title}
                </h3>
                <p className="leading-relaxed text-slate-600">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Courses;
